import React, { useEffect, useMemo, useState } from "react";
import { executeMutation, executeQuery } from "../../../graphql/client";

// Jerseys tab (epic #307 phase 5): one list of everyone who is owed
// the group jersey — Season Pass holders (the pass includes it) and
// monthly members from the start of month 2 (the reason to stay past
// month 1) — plus who is up next, who already has one, and the rows
// that need David's call (comps, paused subs). Month standing comes
// from Stripe via the read-only jerseyQueue query; the only write is
// the shirt-given toggle (legacy `shirtgiven`, shared with the Season
// Pass tab). The order sheet and sponsor pitch are copy-paste text
// built from the live counts — the sponsor logo slot is on the
// jersey itself (sleeve / upper back), not in code.

const OCEAN = "var(--ab-ocean, #0e7c9b)";
const SEAFOAM = "var(--ab-seafoam, #e7f4f3)";

const QUEUE_QUERY = `query JerseyQueue {
  jerseyQueue {
    stripeChecked generatedAt
    rows {
      userId name email sex shirtSize shirtGiven shirtGivenAt
      bucket reason reasonText since earnsOn monthsIn passActive paidMember
    }
  }
}`;

const SET_SHIRT = `mutation SetShirtGivenFromJerseys($userId: ID!, $given: Boolean!) {
  setShirtGiven(userId: $userId, given: $given) { userId shirtGiven }
}`;

const SIZE_ORDER = ["XS", "S", "M", "L", "XL", "2XL", "XXL", "3XL", "XXXL"];

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const normSize = (s) => {
  const v = String(s || "").trim().toUpperCase().replace(/\s+/g, "");
  if (!v) return "";
  if (v === "XXL") return "2XL";
  if (v === "XXXL") return "3XL";
  return v;
};

const sizeRank = (s) => {
  const i = SIZE_ORDER.indexOf(s);
  return i === -1 ? 99 : i;
};

const card = {
  border: "1px solid #e6e9f0",
  borderRadius: 10,
  background: "#fff",
  padding: "12px 14px",
  marginBottom: 14,
};
const muted = { color: "#6a7385", fontSize: 13 };
const th = {
  textAlign: "left",
  fontSize: 12,
  color: "#6a7385",
  fontWeight: 600,
  padding: "4px 6px",
  borderBottom: "1px solid #e6e9f0",
  whiteSpace: "nowrap",
};
const td = { padding: "6px", fontSize: 14, verticalAlign: "top" };

const pill = (active) => ({
  border: active ? `2px solid ${OCEAN}` : "1px solid #d7dbe4",
  background: active ? SEAFOAM : "#fff",
  color: "#333",
  borderRadius: 999,
  padding: "6px 14px",
  fontSize: 13,
  fontWeight: active ? 700 : 500,
  cursor: "pointer",
});

const tag = (bg, fg) => ({
  background: bg,
  color: fg,
  borderRadius: 999,
  padding: "2px 9px",
  fontSize: 12,
  fontWeight: 700,
  whiteSpace: "nowrap",
});

const REASON_TAG = {
  season_pass: ["#e4f5e8", "#1f7a3a", "Season Pass"],
  month_2: ["#e7f4f3", "#0e7c9b", "Month 2+"],
  month_1: ["#fff8e6", "#8a6100", "Month 1"],
  trial: ["#fff8e6", "#8a6100", "Trial"],
  past_due: ["#fdecea", "#b00020", "Past due"],
  paused: ["#f0f2f6", "#6a7385", "Paused"],
  comped: ["#f3e8ff", "#6b21a8", "Comped?"],
  stripe_mismatch: ["#f3e8ff", "#6b21a8", "Mismatch"],
  stripe_unavailable: ["#f0f2f6", "#6a7385", "Stripe down"],
  given: ["#f0f2f6", "#6a7385", "Given"],
};

const Stat = ({ label, value, sub }) => (
  <div style={{ ...card, marginBottom: 0, flex: "1 1 130px", padding: "10px 12px" }}>
    <div style={{ fontSize: 12, color: "#6a7385", fontWeight: 600 }}>{label}</div>
    <div style={{ fontSize: 24, fontWeight: 800, color: "#1f2330" }}>{value}</div>
    {sub && <div style={{ fontSize: 12, color: "#8a93a3" }}>{sub}</div>}
  </div>
);

const CopyBlock = ({ title, text, note, onCopy, copied }) => (
  <div style={card}>
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        flexWrap: "wrap",
        marginBottom: 6,
      }}
    >
      <strong style={{ fontSize: 14 }}>{title}</strong>
      <button type="button" onClick={onCopy} style={{ ...pill(false), padding: "4px 12px" }}>
        Copy
      </button>
      {copied && <span style={muted}>{copied}</span>}
    </div>
    {note && <div style={{ ...muted, marginBottom: 6 }}>{note}</div>}
    <pre
      style={{
        whiteSpace: "pre-wrap",
        fontFamily: "inherit",
        fontSize: 13,
        background: "#f7f8fb",
        borderRadius: 8,
        padding: "8px 10px",
        margin: 0,
      }}
    >
      {text}
    </pre>
  </div>
);

const SECTIONS = [
  {
    id: "owed",
    title: "Owed a jersey",
    blurb:
      "Season Pass holders (the pass includes it) and monthly members from the start of month 2. Mark given when it's handed over.",
    empty: "Nobody is owed a jersey right now.",
  },
  {
    id: "review",
    title: "Your call",
    blurb:
      "Paying by the row but not by Stripe (comps, legacy payers) or paused. The app can't tell which month they're in — decide, then mark given if you hand one over.",
    empty: "",
  },
  {
    id: "upcoming",
    title: "Up next",
    blurb: "Month-1 members: earn theirs at the first renewal. Order for these too if a run is going out.",
    empty: "",
  },
  {
    id: "given",
    title: "Given",
    blurb: "",
    empty: "No jerseys handed out yet.",
  },
];

const JerseysTab = () => {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [copied, setCopied] = useState("");
  const [open, setOpen] = useState({ owed: true, review: true, upcoming: true, given: false });

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const out = await executeQuery(QUEUE_QUERY);
      setRows(out?.jerseyQueue?.rows || []);
      setMeta(out?.jerseyQueue || null);
    } catch (err) {
      setError(err.message || "Couldn't load the jersey queue.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const byBucket = useMemo(() => {
    const g = { owed: [], review: [], upcoming: [], given: [] };
    for (const r of rows) (g[r.bucket] || g.review).push(r);
    return g;
  }, [rows]);

  const owed = byBucket.owed;
  const missingSize = useMemo(
    () => owed.filter((r) => !normSize(r.shirtSize)),
    [owed]
  );

  const sizeCounts = useMemo(() => {
    const counts = {};
    for (const r of owed) {
      const s = normSize(r.shirtSize) || "?";
      counts[s] = (counts[s] || 0) + 1;
    }
    return Object.entries(counts).sort((a, b) => sizeRank(a[0]) - sizeRank(b[0]));
  }, [owed]);

  const sizeLine = (list) => {
    const counts = {};
    for (const r of list) {
      const s = normSize(r.shirtSize) || "?";
      counts[s] = (counts[s] || 0) + 1;
    }
    return Object.entries(counts)
      .sort((a, b) => sizeRank(a[0]) - sizeRank(b[0]))
      .map(([s, n]) => `${s === "?" ? "size unknown" : s} ${n}`)
      .join(" · ");
  };

  const orderSheet = useMemo(() => {
    const lines = [];
    lines.push(`Active Beaches jerseys — order sheet (${fmtDate(new Date().toISOString())})`);
    lines.push(`Owed now: ${owed.length}${owed.length ? ` — ${sizeLine(owed)}` : ""}`);
    if (byBucket.upcoming.length) {
      lines.push(
        `Up next (month-1 members): ${byBucket.upcoming.length} — ${sizeLine(byBucket.upcoming)}`
      );
    }
    if (byBucket.review.length) {
      lines.push(`Your call (comps / paused): ${byBucket.review.length} — ${sizeLine(byBucket.review)}`);
    }
    lines.push(`Already given: ${byBucket.given.length}`);
    if (missingSize.length) {
      lines.push(
        `Ask for a size: ${missingSize
          .map((r) => r.name || r.email || r.userId)
          .join(", ")}`
      );
    }
    lines.push("Design: one group jersey; sponsor logo slot on the sleeve / upper back.");
    return lines.join("\n");
  }, [owed, byBucket, missingSize]);

  const sponsorPitch = useMemo(() => {
    const onCourts = byBucket.given.length;
    const goingOut = owed.length + byBucket.upcoming.length;
    return [
      "Your logo on every Active Beaches jersey.",
      `${onCourts} jersey${onCourts === 1 ? "" : "s"} already on the courts, ${goingOut} more going out this season — worn at every weekly tourney night, the monthly art walks, and in every event photo we post.`,
      "The ask: $___/month, and your logo goes on the sleeve of every jersey in the next run. One sponsor per run.",
    ].join("\n");
  }, [owed, byBucket]);

  const copyText = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(`Copied ${label}`);
    } catch (err) {
      setCopied("Couldn't copy — select the text instead");
    }
    setTimeout(() => setCopied(""), 2500);
  };

  const copyEmails = (list, label) => {
    const emails = [...new Set(list.map((r) => r.email).filter(Boolean))];
    return copyText(emails.join(", "), `${emails.length} ${label}`);
  };

  const toggleShirt = async (r) => {
    setBusy(r.userId);
    setError("");
    try {
      await executeMutation(SET_SHIRT, { userId: r.userId, given: !r.shirtGiven });
      // Re-read so the row moves to / from the Given section with its
      // real reason and timestamp instead of a guessed one.
      await load();
    } catch (err) {
      setError(err.message || "Couldn't update the shirt flag.");
    } finally {
      setBusy("");
    }
  };

  const renderTable = (list, sectionId) => (
    <div style={{ ...card, overflowX: "auto", padding: 0 }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={th}>Name</th>
            <th style={th}>Email</th>
            <th style={th}>Why</th>
            <th style={th}>{sectionId === "given" ? "Given" : sectionId === "upcoming" ? "Earns" : "Since"}</th>
            <th style={th}>Size</th>
            <th style={th}>Jersey</th>
          </tr>
        </thead>
        <tbody>
          {list.map((r) => {
            const [bg, fg, label] = REASON_TAG[r.reason] || REASON_TAG.given;
            const size = normSize(r.shirtSize);
            return (
              <tr key={r.userId} style={{ borderBottom: "1px solid #f0f2f6" }}>
                <td style={td}>
                  {r.name || <span style={muted}>(no name)</span>}
                  {r.passActive && r.reason !== "season_pass" && (
                    <span title="Also holds a live Season Pass" style={{ marginLeft: 6 }} role="img" aria-label="season pass">
                      🎫
                    </span>
                  )}
                </td>
                <td style={{ ...td, wordBreak: "break-all" }}>{r.email || ""}</td>
                <td style={td}>
                  <span style={tag(bg, fg)}>{label}</span>
                  <div style={{ ...muted, marginTop: 2 }}>{r.reasonText}</div>
                </td>
                <td style={{ ...td, whiteSpace: "nowrap" }}>
                  {sectionId === "given"
                    ? fmtDate(r.shirtGivenAt) || "—"
                    : sectionId === "upcoming"
                    ? fmtDate(r.earnsOn) || "—"
                    : fmtDate(r.since) || "—"}
                </td>
                <td style={td}>
                  {size || (
                    <span style={tag("#fdecea", "#b00020")}>ask</span>
                  )}
                </td>
                <td style={{ ...td, whiteSpace: "nowrap" }}>
                  <button
                    type="button"
                    onClick={() => toggleShirt(r)}
                    disabled={busy === r.userId}
                    style={{ ...pill(r.shirtGiven), padding: "3px 10px", fontSize: 12 }}
                  >
                    {r.shirtGiven ? "✓ given · undo" : "mark given"}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  return (
    <div style={{ maxWidth: 960 }}>
      <h2 style={{ marginTop: 0 }}>
        <span role="img" aria-label="shirt">👕</span> Jerseys
      </h2>
      <p style={muted}>
        Who is owed the group jersey. The rule: a <strong>Season Pass</strong> includes
        it from day one; a <strong>monthly member</strong> earns it at the start of
        month 2 (read from Stripe — the second payment went through). Women on the
        free tier aren't in the program unless they bought a pass. Jersey orders
        and sizes come from here; the sponsor logo slot lives on the shirt
        (sleeve / upper back).
      </p>

      {error && (
        <div
          style={{
            background: "#fdecea",
            border: "1px solid #e7b3ba",
            color: "#b00020",
            borderRadius: 8,
            padding: "10px 14px",
            marginBottom: 12,
            fontSize: 14,
          }}
        >
          {error}
        </div>
      )}

      {meta && meta.stripeChecked === false && (
        <div
          style={{
            ...card,
            background: "#fff8e6",
            borderColor: "#f1d9a0",
            fontSize: 14,
          }}
        >
          <strong>Stripe couldn't be read</strong> — monthly members show under
          "Your call" until it comes back. Season Pass holders and given rows are
          unaffected.
        </div>
      )}

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
        <Stat label="Owed now" value={owed.length} sub={sizeCounts.map(([s, n]) => `${s} ${n}`).join(" · ") || undefined} />
        <Stat label="Your call" value={byBucket.review.length} sub="comps / paused" />
        <Stat label="Up next" value={byBucket.upcoming.length} sub="month-1 members" />
        <Stat label="Given" value={byBucket.given.length} />
        <Stat label="Missing a size" value={missingSize.length} sub="of those owed" />
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 14 }}>
        <button
          type="button"
          onClick={() => copyEmails(owed, "owed emails")}
          disabled={!owed.length}
          style={{ ...pill(false), opacity: owed.length ? 1 : 0.5 }}
        >
          Copy owed emails
        </button>
        <button
          type="button"
          onClick={() => copyEmails(missingSize, "ask-for-size emails")}
          disabled={!missingSize.length}
          style={{ ...pill(false), opacity: missingSize.length ? 1 : 0.5 }}
        >
          Copy ask-for-size emails
        </button>
        <button type="button" onClick={load} style={pill(false)}>
          Refresh
        </button>
        {copied && <span style={muted}>{copied}</span>}
        {meta?.generatedAt && (
          <span style={{ ...muted, marginLeft: "auto" }}>
            as of {new Date(meta.generatedAt).toLocaleTimeString()}
          </span>
        )}
      </div>

      {loading && !rows.length && <p style={muted}>Loading… (reads Stripe, a few seconds)</p>}

      {!loading &&
        SECTIONS.map((s) => {
          const list = byBucket[s.id];
          if (!list.length && !s.empty) return null;
          return (
            <div key={s.id} style={{ marginBottom: 18 }}>
              <div
                style={{ display: "flex", alignItems: "baseline", gap: 10, cursor: "pointer" }}
                onClick={() => setOpen((o) => ({ ...o, [s.id]: !o[s.id] }))}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setOpen((o) => ({ ...o, [s.id]: !o[s.id] }));
                  }
                }}
              >
                <h3 style={{ margin: "0 0 4px" }}>
                  {open[s.id] ? "▾" : "▸"} {s.title}{" "}
                  <span style={{ ...muted, fontWeight: 500 }}>({list.length})</span>
                </h3>
              </div>
              {s.blurb && <p style={{ ...muted, marginTop: 0 }}>{s.blurb}</p>}
              {open[s.id] &&
                (list.length ? renderTable(list, s.id) : <p style={muted}>{s.empty}</p>)}
            </div>
          );
        })}

      {!loading && (
        <>
          <CopyBlock
            title="Order sheet"
            note="Paste to the shirt vendor. Counts are the owed list by size; 'Up next' is who earns one before the next run lands."
            text={orderSheet}
            onCopy={() => copyText(orderSheet, "the order sheet")}
            copied={copied}
          />
          <CopyBlock
            title="Sponsor pitch"
            note="Fill in the monthly number before you send it. One sponsor per jersey run; the slot is the sleeve / upper back."
            text={sponsorPitch}
            onCopy={() => copyText(sponsorPitch, "the sponsor pitch")}
            copied={copied}
          />
        </>
      )}
    </div>
  );
};

export default JerseysTab;
